import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { routeConstant } from "shared/routes/routeConstant";
import { useNavigate } from "react-router-dom";
import ProjectsList from "shared/components/dashboardCommon/projectsList";
import ProjectNotificationBanner from "shared/components/dashboardCommon/projectNotificationBanner";
import ProjectNotificationBannerCarousel, {
  BannerItem,
} from "shared/components/dashboardCommon/projectNotificationBannerCarousel";
import { GetClientOnboardingProjectService } from "shared/services/project";
import { ProjectDetailTabs } from "shared/utils/enum";
import { MarkRevisionViewedService } from "shared/services/revision";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { selectPendingOnboarding } from "shared/utils/pendingOnboarding";

const ClientProjects = () => {
  const auth = useSelector((state: any) => state.root.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [projects, setProjects] = useState<any[]>([]);
  const [onboardingProjects, setOnboardingProjects] = useState<any[]>(
    selectPendingOnboarding(auth?.onboarding_projects),
  );

  useEffect(() => {
    let cancelled = false;
    const fetchOnboarding = async () => {
      try {
        const response = await GetClientOnboardingProjectService();
        const raw = response.data?.data ?? response.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const active = selectPendingOnboarding(list);
        setOnboardingProjects(active);
        dispatch(
          setAuthReducer({
            onboarding_projects: active,
            onboarding_project: active[0] || null,
          }),
        );
      } catch {
        // Persisted state can name a deleted project, so a failed refresh must clear the banner.
        if (!cancelled) setOnboardingProjects([]);
      }
    };
    fetchOnboarding();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handleResumeOnboarding = (project: any) => {
    sessionStorage.removeItem("onboarding_dismissed");
    navigate(routeConstant.onboarding.path, {
      state: { projectId: project?.project_id },
    });
  };

  const handleProjectsLoaded = (loadedProjects: any[]) => {
    setProjects(loadedProjects);
  };

  const navigateToProject = (
    project: any,
    options: { tab?: ProjectDetailTabs } = {},
  ) => {
    const path = routeConstant.client.projectDetail.path.replace(
      ":id",
      String(project.project_id),
    );
    navigate(options.tab ? `${path}?tab=${options.tab}` : path, {
      state: { projectData: project },
    });
  };

  const reviewItems: BannerItem[] = projects
    .filter((p: any) => p.latest_revision)
    .filter((p: any) => (p.status || "").toLowerCase() !== "completed")
    .filter(
      (p: any) =>
        (p.latest_revision?.status || "").toLowerCase() ===
        "waiting_for_client_response",
    )
    .filter((p: any) => !p.latest_revision?.viewed_by_current_user)
    .slice()
    .sort((a: any, b: any) => {
      const aTime = new Date(
        a.latest_revision?.created_at || a.created_at || 0,
      ).getTime();
      const bTime = new Date(
        b.latest_revision?.created_at || b.created_at || 0,
      ).getTime();
      return bTime - aTime;
    })
    .map((p: any) => ({
      key: p.project_id,
      title: `🎉 ${p.name || ""} - ${
        p.latest_revision?.title || "Latest Revision"
      } is ready for your review!`,
      // The only action: open the latest revision on Revisions & Media and
      // mark it viewed, retiring the card.
      onButtonClick: () =>
        navigateToProject(p, { tab: ProjectDetailTabs.REVISIONS_MEDIA }),
      onDismiss: () => {
        if (p.latest_revision?.revision_id) {
          MarkRevisionViewedService(Number(p.latest_revision.revision_id)).catch(
            () => {},
          );
        }
      },
    }));

  const notificationBanner =
    onboardingProjects.length > 0 ? (
      <ProjectNotificationBanner
        title={
          onboardingProjects.length === 1
            ? `Finish setting up ${onboardingProjects[0].name || "your project"}`
            : `Finish setting up your projects (${onboardingProjects.length} awaiting onboarding)`
        }
        description="You haven't completed onboarding yet. Resume to continue — we'll take you through each project one at a time."
        buttonText="Complete Onboarding"
        onButtonClick={() => handleResumeOnboarding(onboardingProjects[0])}
      />
    ) : reviewItems.length > 0 ? (
      <ProjectNotificationBannerCarousel items={reviewItems} />
    ) : null;

  return (
    <ProjectsList
      userType="client"
      pageTitle={`Welcome back, ${auth?.user?.display_name || ""}!`}
      pageDescription="Here's where your home design stands today"
      showSearchFilter={true}
      showNotificationBanner={!!notificationBanner}
      notificationBannerContent={notificationBanner}
      EnableApiIntegration={true}
      onProjectsLoaded={handleProjectsLoaded}
      noContentMessage="You don't have any projects at the moment. Check back later."
      detailRoutePath={routeConstant.client.projectDetail.path}
    />
  );
};

export default ClientProjects;
